import { state } from "../state";
import type { ScreenContext } from "../app";
import { renderHeader } from "../components";
import { formatPlayerName, goToChangeNickname } from "../playerName";
import {
  bonusPercent,
  levelFromXp,
  maxFleetSize,
  MAX_LEVEL,
  SKILL_NAMES,
  xpForLevel,
  xpToNextLevel
} from "../../captainSkills";
import { missionSuccessRate, missionsResolved } from "../../captainStats";
import { trophySummary } from "./trophyRoomScreen";

// Captain's Lounge -- **repurposed in 16.76**. It used to be the
// hired-ship rental board; hiring moved to the Shipyard when the separate
// captain-recruitment concept was dropped (captains now simply come with
// their ships). What's left is the one thing the game had nowhere to put:
// a screen about the CAPTAIN rather than about a hull.
//
// Free and always present at every Station and player Base -- no build
// cost, no lot to purchase (deliberately absent from
// BASE_LOT_BUILD_COST_CR, which is what makes it always-built). Also
// reachable in flight via the Expedition View's Bridge icon, since a
// captain shouldn't have to dock to look at their own record.
//
// Everything here is derived live -- skill levels/bonuses from
// captainSkills.ts, career totals from state.stats' raw counters, fleet
// and exploration figures from the real state they already live in. No
// value is stored twice, so nothing shown here can drift from what the
// game actually did.
export function renderCaptainsLoungeScreen(container: HTMLElement, ctx: ScreenContext): void {
  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  container.appendChild(wrap);

  const stats = state.stats;
  const resolved = missionsResolved(stats);
  const successRate = missionSuccessRate(stats);
  const commandLevel = levelFromXp(state.captainSkills.Command);
  const escorts = state.fleet.filter((f) => f.id !== state.currentShipInstanceId).length;
  const trophy = trophySummary();

  function statRow(label: string, value: string): string {
    return `<div class="cargo-row"><label><span class="cargo-row-name">${label}</span></label><span class="dim">${value}</span></div>`;
  }

  const skillRows = SKILL_NAMES.map((name) => {
    const xp = state.captainSkills[name];
    const level = levelFromXp(xp);
    const toNext = xpToNextLevel(xp);
    const label = name === "TractorBeam" ? "Tractor Beam" : name;
    // Progress within the CURRENT level, not toward the lifetime total --
    // "83% of the way to level 4" is the useful reading, and it's derived
    // from the same xpForLevel curve the level itself comes from.
    const floor = xpForLevel(level);
    const ceil = level >= MAX_LEVEL ? floor : xpForLevel(level + 1);
    const pct = level >= MAX_LEVEL ? 100 : ((xp - floor) / (ceil - floor)) * 100;
    return `
      <div class="cargo-row">
        <label><span class="cargo-row-name">${label}</span></label>
        <span class="dim">Level ${level}/${MAX_LEVEL} &middot; +${bonusPercent(xp)}%</span>
        <div class="xv-bar-track" style="flex:1; min-width:80px;">
          <div class="xv-bar-fill hull-healthy" style="width:${pct.toFixed(0)}%"></div>
        </div>
        <span class="dim">${toNext === null ? "maxed" : `${Math.ceil(toNext).toLocaleString()} XP to go`}</span>
      </div>
    `;
  }).join("");

  const name = formatPlayerName(state.displayName ?? state.username);

  wrap.innerHTML = `
    <h1>Captain's Lounge</h1>
    <p class="dim">
      Your own record, ${state.username ? `Captain ${name.text}` : "Captain"}
      ${name.opaque ? '<button type="button" class="mm-btn-changename" data-change-name>Change Name</button>' : ""}
      -- skills earned,
      contracts run, and what it cost you. Free to use at any Station or Base, and from the Bridge while under way.
    </p>

    <h2>Career</h2>
    <div class="cargo-list">
      ${statRow("Missions completed", stats.missionsCompleted.toLocaleString())}
      ${statRow("Missions abandoned", stats.missionsAbandoned.toLocaleString())}
      ${statRow(
        "Success rate",
        successRate === null
          ? "no missions resolved yet"
          : `${Math.round(successRate * 100)}% of ${resolved.toLocaleString()}`
      )}
      ${statRow("Hostiles destroyed", stats.hostilesDestroyed.toLocaleString())}
      ${statRow("Drones lost", stats.dronesLost.toLocaleString())}
      ${statRow("Ore mined", `${Math.round(stats.oreMinedM3).toLocaleString()} m3`)}
    </div>

    <h2>Command</h2>
    <div class="cargo-list">
      ${statRow("Credits", `${Math.round(state.credits).toLocaleString()} CR`)}
      ${statRow("Fleet", `${state.fleet.length} ship${state.fleet.length === 1 ? "" : "s"}`)}
      ${statRow(
        "Escorts",
        `${escorts}/${maxFleetSize(state.captainSkills.Command)} (Command level ${commandLevel})`
      )}
      ${statRow("Systems visited", `${state.visitedSystemIds.length} of 107`)}
      ${statRow("Bases owned", state.bases.length.toLocaleString())}
    </div>

    <h2>Trophy Room</h2>
    <p class="dim">
      The other half of a captain's record: not what you did, but what you found. ${trophy.mysteriesFound} of
      ${trophy.mysteriesTotal} unlisted systems reached, ${trophy.opponentsMet} of ${trophy.opponentsTotal}
      opponents identified.
    </p>
    <button type="button" data-open-trophies>Open the Trophy Room</button>

    <h2>Skills</h2>
    <p class="dim">
      1 XP per second of real engagement -- mining with the laser firing, fighting with a weapon on
      target, piloting under way. A deliberate nudge (max +10%), never a substitute for better hardware.
    </p>
    <div class="cargo-list">${skillRows}</div>
  `;

  wrap.querySelector<HTMLButtonElement>("[data-change-name]")?.addEventListener("click", goToChangeNickname);
  wrap.querySelector<HTMLButtonElement>("[data-lounge-back]")?.addEventListener("click", () => {
    ctx.navigate("expedition");
  });
  wrap.querySelector<HTMLButtonElement>("[data-open-trophies]")?.addEventListener("click", () => {
    ctx.navigate("trophies");
  });
}
